import React, { useState, useEffect } from 'react';
import { Select, Flex } from 'antd';
import { useReactFlow, useStore } from '@xyflow/react';
import {
  TimeDelayParams,
  EventCompletedParams,
  InteractionParams,
  CounterReachedParams,
  CollisionParams,
  SceneLoadedParams,
  CustomParams,
} from './conditionParams/index';
import { ConditionType } from '@src/components/generated/graphql';
import { INTERACTION_TYPES } from 'util/constants';
import { NODES_TYPES } from '../../types';
import type { EventCondition } from '@src/components/generated/graphql';
import styles from '../../event-timeline.module.css';

const { Option } = Select;

interface ConditionParamsProps {
  nodeId: string;
  selectableAssets: any[];
}

const ConditionParams: React.FC<ConditionParamsProps> = ({
  nodeId,
  selectableAssets,
}) => {
  const { getNodes, setNodes } = useReactFlow();
  const node = useStore((state) => state.nodes.find((n) => n.id === nodeId));
  const condition = node?.data ?? ({} as any);
  const [type, setType] = useState(condition.type);
  const [params, setParams] = useState(condition.params);

  const onChangeNode = (key: string, value: string) => {
    setNodes((nodes) =>
      nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                [key]: value,
              },
            }
          : n
      )
    );
  };

  function onChangeParams(params: any) {
    setParams(params);
    onChangeNode('params', params);
  }

  const handleTypeChange = (value: EventCondition['type']) => {
    setType(value);
    onChangeNode('type', value);
    switch (value) {
      case ConditionType.TimeDelay:
        onChangeParams({ delay: 1000 });
        break;
      case ConditionType.EventCompleted:
        onChangeParams({ eventId: '' });
        break;
      case ConditionType.Interaction:
        onChangeParams({
          targetId: '',
          interactionType: INTERACTION_TYPES.COLLISION,
        });
        break;
      case ConditionType.CounterReached:
        onChangeParams({ counterId: '', value: 1 });
        break;
      case ConditionType.Collision:
        onChangeParams({ objectId: '', targetId: '' });
        break;
      case ConditionType.SceneLoaded:
        onChangeParams({});
        break;
      case ConditionType.Custom:
        onChangeParams({});
        break;
    }
  };

  const renderParamsInputs = () => {
    switch (type) {
      case ConditionType.TimeDelay:
        return <TimeDelayParams params={params} setParams={onChangeParams} />;

      case ConditionType.EventCompleted:
        return (
          <EventCompletedParams
            params={params}
            setParams={onChangeParams}
            allEvents={getNodes().filter(
              (node) => node.type === NODES_TYPES.EVENT
            )}
          />
        );

      case ConditionType.Interaction:
        return (
          <InteractionParams
            params={params}
            setParams={onChangeParams}
            selectableAssets={selectableAssets}
          />
        );

      case ConditionType.CounterReached:
        return (
          <CounterReachedParams params={params} setParams={onChangeParams} />
        );

      case ConditionType.Collision:
        return <CollisionParams params={params} setParams={onChangeParams} />;

      case ConditionType.SceneLoaded:
        return <SceneLoadedParams params={params} setParams={onChangeParams} />;

      case ConditionType.Custom:
        return <CustomParams params={params} setParams={onChangeParams} />;

      default:
        return null;
    }
  };

  useEffect(() => {
    setParams(condition.params);
    setType(condition.type);
  }, [node?.id]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <Flex gap={8} vertical>
            <span className={styles.label}>Type:</span>
            <Select
              value={type}
              onChange={handleTypeChange}
              style={{ width: '100%' }}
            >
              <Option value={ConditionType.SceneLoaded}>Scene Loaded</Option>
              <Option value={ConditionType.TimeDelay}>Time Delay</Option>
              <Option value={ConditionType.Interaction}>Interaction</Option>
              <Option value={ConditionType.CounterReached}>
                Counter Reached
              </Option>
            </Select>
          </Flex>
        </div>
      </div>

      {renderParamsInputs()}
    </>
  );
};

export default ConditionParams;
